import React, { useEffect, useState } from "react";
import { Input, DatePicker, Button, Skeleton, message } from "antd";
import moment from "moment";
import { customDateTimeFormat } from "../../../utils/filterHelper";
import { isFilterActive } from "../../../utils/dataFilterEngine";
import { DATE_WITH_HOUR_FORMAT, DATE_WITH_HOUR_MINUTES_FORMAT } from "../../../constants/dateConstants";
import "./filterPanels.css";

/**
 * Data filters on the board view - the controls a reader uses to narrow rows that are
 * already loaded. Nothing here calls the API; Apply hands the resource back up to the
 * board, and the table widget re-filters in memory (src/utils/dataFilterEngine.js).
 *
 * Replaces Filters/index.js, which is kept for revert. Beyond the restyle this adds
 * what that panel had no notion of: validation, an active-filter count, an explicit
 * empty state, and Between ranges that are checked before they are applied.
 */

const OPERATOR_LABELS = {
  equal: "Equals",
  con: "Contains",
  gt: "Greater than",
  gte: "Greater or equal",
  lt: "Less than",
  lte: "Less or equal",
  between: "Between",
};

const resourceKey = (resource, index) => resource?.connectionData?.id ?? resource?.id ?? index;

/** Everything wrong with one filter's current input, as a message or null. */
function validateFilter(filter) {
  const isBetween = filter.filterType === "between";
  const filled = (v) => v !== null && v !== undefined && v !== "";

  if (isBetween) {
    // Half a range is not a filter - say so rather than silently ignoring it.
    if (filled(filter.value1) !== filled(filter.value2)) return "Enter both ends of the range.";
    if (!filled(filter.value1)) return null;

    if (filter.dataType === "number" && Number(filter.value1) > Number(filter.value2)) {
      return "From must not be greater than To.";
    }
    if (filter.dataType === "date" && moment(filter.value1).isAfter(moment(filter.value2))) {
      return "From date must not be after To date.";
    }
    return null;
  }

  if (filled(filter.value) && filter.dataType === "number" && Number.isNaN(Number(filter.value))) {
    return "Enter a number.";
  }

  return null;
}

export default function DataFiltersPanel({ board, onApplyDataFilter, onResetDataFilter }) {
  const [resources, setResources] = useState(null);

  useEffect(() => {
    /* Was set once on mount, so opening a different board left the previous board's
       filters on screen until a reload. */
    setResources(board?.resources || null);
  }, [board]);

  if (!board) return <Skeleton />;
  if (!resources) return null;

  const setFilterValue = (resource, filter, key, value) => {
    setResources((current) =>
      current.map((r) =>
        resourceKey(r) !== resourceKey(resource)
          ? r
          : {
              ...r,
              dataFilters: (r.dataFilters || []).map((f) => (f.id === filter.id ? { ...f, [key]: value } : f)),
            },
      ),
    );
  };

  const clearResourceFilters = (resource) => {
    const cleared = {
      ...resource,
      /* Was: dates reset to today rather than to empty, so a "cleared" date filter
         still matched only today's rows. */
      dataFilters: (resource.dataFilters || []).map((f) => ({ ...f, value: null, value1: null, value2: null })),
    };

    setResources((current) => current.map((r) => (resourceKey(r) === resourceKey(resource) ? cleared : r)));
    onResetDataFilter(cleared);
  };

  const applyResourceFilters = (resource) => {
    const errors = (resource.dataFilters || []).map(validateFilter).filter(Boolean);
    if (errors.length > 0) {
      message.error(errors[0]);
      return;
    }

    if (!(resource.dataFilters || []).some(isFilterActive)) {
      message.info("Enter a value in at least one filter first.");
      return;
    }

    onApplyDataFilter(resource);
  };

  const renderInput = (resource, filter, key) => {
    const value = filter[key] ?? "";
    const onChange = (v) => setFilterValue(resource, filter, key, v);

    if (filter.dataType === "date") {
      /* Follows the column's declared format, so a column holding a timestamp gets a
         time picker and the value saved matches what the table shows. */
      const format = customDateTimeFormat(resource.availableFields).getDateFormat(filter.fieldName).dateAndTimeFormat;

      return (
        <DatePicker
          value={filter[key] ? moment(filter[key]) : null}
          format={format}
          showTime={format === DATE_WITH_HOUR_FORMAT || format === DATE_WITH_HOUR_MINUTES_FORMAT}
          onChange={(date, dateString) => onChange(dateString || null)}
          placeholder="Select date"
        />
      );
    }

    if (filter.dataType === "number") {
      return (
        <Input type="number" value={value} placeholder="Value" onChange={(e) => onChange(e.target.value || null)} />
      );
    }

    return <Input value={value} placeholder="Value" onChange={(e) => onChange(e.target.value || null)} />;
  };

  return (
    <div className="vf-panel">
      {resources.map((resource, i) => {
        const filters = resource.dataFilters || [];
        const activeCount = filters.filter(isFilterActive).length;

        return (
          <div className="vf-resource" key={`vf_data_${resourceKey(resource, i)}`}>
            <div className="vf-resource-head">
              <span className="vf-resource-name">{resource.connectionData?.name || `Resource ${i + 1}`}</span>
              {activeCount > 0 ? (
                <span className="vf-active-count">
                  {activeCount} active {activeCount === 1 ? "filter" : "filters"}
                </span>
              ) : null}
            </div>

            {filters.length === 0 ? (
              <div className="vf-empty">
                <span className="vf-empty-title">No data filters</span>
                Add them in the editor's Filters section to narrow this report's rows here.
              </div>
            ) : (
              <>
                <div className="vf-fields">
                  {filters.map((filter) => {
                    const error = validateFilter(filter);

                    return (
                      <div className="vf-field" key={`vf_f_${filter.id}`}>
                        <span className="vf-field-label">
                          {filter.displayName || filter.fieldName}
                          <span className="vf-op">{OPERATOR_LABELS[filter.filterType] || filter.filterType}</span>
                        </span>

                        {filter.filterType === "between" ? (
                          <div className="vf-range">
                            {renderInput(resource, filter, "value1")}
                            <span className="vf-range-sep">to</span>
                            {renderInput(resource, filter, "value2")}
                          </div>
                        ) : (
                          renderInput(resource, filter, "value")
                        )}

                        {error ? <span className="vf-error">{error}</span> : null}
                      </div>
                    );
                  })}
                </div>

                <div className="vf-actions">
                  <Button type="primary" onClick={() => applyResourceFilters(resource)}>
                    Apply
                  </Button>
                  <Button onClick={() => clearResourceFilters(resource)} disabled={activeCount === 0}>
                    Reset
                  </Button>
                  {activeCount === 0 ? <span className="vf-hint">Enter a value to filter.</span> : null}
                </div>
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}